
// Tracks proposed changes for a given user's careplan section
var Model = require("../types/Model.js");
var xtend = require("xtend");
var par = require("par");

var PERSON_KEYS = [
	"created_by",
	"confirmed_by",
	"committed_by",
	"owner"
];

var TIME_KEYS = [
	"created_at",
	"updated_at",
	"confirmed_at",
	"committed_at"
];

module.exports = Model({
	name: "managedcareplanproposedchanges",
	init: init,
	uninit: uninit,
	listeners: listeners,
	triggers: triggers,

	_who: "",
	_section: "",
	_who_tracker: null,
	_list: null,
	_people: null,
	_group: "",

	refresh: refresh,
	reset: reset,
	track: track,
	trackIds: trackIds,
	recalculate: recalculate,
});

function listeners() {
	return {
		"connectivity:online": refresh,
		"people:updated": Model.makeRecalculator("people"),
	};
}

function triggers() {
	return {
		view: view,
		reset: reset,
		confirm: confirm,
		deny: deny,
	};
}

function init() {
	this._list = [];
	this._people = {};
	this._group = null;

	this._who_tracker = this.dispatcher.createTracker();

	this.reset();
}

function uninit() {
	this._group = null;
	this._list = null;
	this._people = null;
	this.reset();
	this._who_tracker = null;
}

function confirm(data) {
	var who = this._who;
	var group = this._group;

	var section = this._section;
	var change = data.change;
	this.emit("save:trigger:start");
	this.api.manage.careplan[section.toLowerCase()]
		.changes.confirmed.add(who, change, group)
		.catch(console.error.bind(console))
		.then(finishSave.bind(this));
}

function deny(data) {
	var who = this._who;
	var group = this._group;
	var section = this._section;
	var change = data.change;
	this.emit("save:trigger:start");
	this.api.manage.careplan[section.toLowerCase()]
		.changes.denied.add(who, change, group)
		.catch(console.error.bind(console))
		.then(finishSave.bind(this));
}

function track() {
	var tracker = this._who_tracker;
	tracker.off();
	var who = this._who;
	var section = this._section;
	var group = this._group;

	if(!who || !section)
		return;

	tracker.on("mqtt:$/careplan/changes/synchronized", handle_synch.bind(this));

	if(group){
		if(Array.isArray(group)) {
			group.forEach(g=>{
				tracker.on("mqtt:managegroup/" + g + "/patient/u/" + who + "/careplan/" + section + "/changes/proposed", handle_add.bind(this));
				tracker.on("mqtt:managegroup/" + g + "/patient/u/" + who + "/careplan/" + section + "/changes/confirmed", updateItem.bind(this));
				tracker.on("mqtt:managegroup/" + g + "/patient/u/" + who + "/careplan/" + section + "/changes/denied", removeItem.bind(this));
				tracker.on("mqtt:managegroup/" + g + "/patient/u/" + who + "/careplan/" + section + "/changes/committed", updateItem.bind(this));
			});
		} else {
			tracker.on("mqtt:managegroup/" + group + "/patient/u/" + who + "/careplan/" + section + "/changes/proposed", handle_add.bind(this));
			tracker.on("mqtt:managegroup/" + group + "/patient/u/" + who + "/careplan/" + section + "/changes/confirmed", updateItem.bind(this));
			tracker.on("mqtt:managegroup/" + group + "/patient/u/" + who + "/careplan/" + section + "/changes/denied", removeItem.bind(this));
			tracker.on("mqtt:managegroup/" + group + "/patient/u/" + who + "/careplan/" + section + "/changes/committed", updateItem.bind(this));
		}
	} else {
		tracker.on("mqtt:u/" + who + "/careplan/" + section + "/changes/proposed", handle_add.bind(this));
		tracker.on("mqtt:u/" + who + "/careplan/" + section + "/changes/confirmed", updateItem.bind(this));
		tracker.on("mqtt:u/" + who + "/careplan/" + section + "/changes/denied", removeItem.bind(this));
		tracker.on("mqtt:u/" + who + "/careplan/" + section + "/changes/committed", updateItem.bind(this));
	}
}

function handle_synch() {
	if (!this._list) return;
	this._list = this._list.filter(function(item) {
		return item.state !== "committed";
	});
	var people = this._people;
	var list = this._list;
	this.push(list.sort(by_time).map(par(populateItem, people)));
}

function recalculate() {
	if(!(this._who || this._group)) return;
	var list = this._list;
	var people = this._people;

	this.push(list.sort(by_time).map(par(populateItem, people)));
}

function by_time(a, b) {
	var timeA = new Date(a.created_at).getTime();
	var timeB = new Date(b.created_at).getTime();

	return timeA - timeB;
}

function populateItem(people, item) {
	var personUpdate = PERSON_KEYS.reduce(function (update, key) {
		var id = item[key];
		if(!id) return update;
		var data = people[id];
		if(!data) return update;
		update[key] = data;
		return update;
	}, {});

	var timeUpdate = TIME_KEYS.reduce(function (update, key) {
		var time = item[key];
		if(!time) return update;
		update[key] = timestamp(time);
		return update;
	}, {});

	var changeUpdate = {
		changes: processChanges(item)
	};

	return xtend(item, personUpdate, timeUpdate, changeUpdate);
}

function handle_add(data) {
	var list = this._list;
	var all = list.concat(data);
	this._list = all;
	var ids = getItemIds(data);

	this.emit("people:trigger:add", {
		lean: true,
		who: ids
	});

	this.recalculate();
}

function updateItem(data) {
	var id = data._id;
	var items = this._list;
	this._list = items.map(function(item){
		if(item._id === id)
			return xtend(item, data);
		return item;
	});
	this.recalculate();
}

function removeItem(data) {
	var id = data._id;
	var items = this._list;
	this._list = items.filter(function (item) {
		return item._id !== id;
	});
	this.recalculate();
}

function refresh() {
	var who = this._who;
	var section = this._section;
	var group = this._group;
	if(!who || !section)
		return;

	this.emit("save:trigger:start");
	this.api.manage.careplan[section.toLowerCase()]
		.changes.pending.list(who, group)
		.then(handle_loaded.bind(this))
		.catch(console.error.bind(this))
		.then(finishSave.bind(this));
}

function finishSave() {
	this.emit("save:trigger:finish");
}

function handle_loaded(list) {
	this._list = list;
	this.trackIds();
	this.recalculate();
}

function view(data) {
	var who = data.who;
	var group = data.group;
	var section = data.section;
	if(who === this._who && section === this._section)
		return;

	this._who = who;
	this._group = group;
	this._section = section;
	this.track();
	this.refresh();
}

function reset() {
	this._group = "";
	this._who = "";
	this._section = "";
	this._who_tracker.off();
	this.push([]);
}

function trackIds() {
	var list = this._list;
	var ids = getIds(list);
	if(!ids.length) return;
	this.emit("people:trigger:add", {
		who: ids,
		lean: true
	});
}

function getIds(list) {
	return list.map(getItemIds).reduce(concat, []);
}

function getItemIds(item) {
	return PERSON_KEYS.reduce(function(result, key){
		var id = item[key];
		if(!id) return result;
		if(result.indexOf(id) !== -1) return result;
		return result.concat(id);
	}, []);
}

function concat(a, b) {
	return a.concat(b);
}

function processChanges(item) {
	return item.changes.map(processChange);
}

function processChange(change) {
	var path = change.path;
	var processed = path.replace(/\/(\d+|-)/g, "");
	var value = change.value || "";
	return xtend(change, {
		simplePath: processed,
		value: value
	});
}

// Converts a date timestamp into a human readable timestamp
function timestamp(value) {
	if (!value) return ""; // Ignore empty values
	var d = new Date(value);
	var res = "";
	res += padNumber(d.getDate());
	res += "/" + padNumber(d.getMonth() + 1);
	res += " " + padNumber(d.getHours());
	res += ":" + padNumber(d.getMinutes());
	return res;
}

// Pads numbers less than 10 with a 0
function padNumber(number) {
	if (number < 10) return "0" + number;
	return number;
}
